'use client';

import { createContext, useContext } from 'react';

export type ToastSeverity = 'info' | 'warning' | 'error';

export interface ToastEntry {
    id: string;
    message: string;
    severity: ToastSeverity;
    /** How long it shows, in milliseconds; 8 seconds when unset. */
    durationMs?: number;
    /**
     * Small, with no dismiss button, letting clicks through: for frequent
     * feedback during play (a refused action, a pickup) that must not cover
     * or block the game. Only the newest few stay on screen.
     */
    compact?: boolean;
}

export type CreateToastInput = Omit<ToastEntry, 'id'>;

export interface ToastContextValue {
    queue: ToastEntry[];
    /** Shows a toast and returns its id. */
    showToast: (input: CreateToastInput) => string;
    dismissToast: (id: string) => void;
}

export const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/** The toast queue and its actions. The value changes only when the queue does. */
export function useToast(): ToastContextValue {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}
